import React, { useEffect, useState } from "react";
import MetaMaskErrorRecovery from "../metamask-error-recovery/MetaMaskErrorRecovery";

/**
 * Global MetaMask error handler that listens for MetaMask connection errors
 * throughout the application and shows the error recovery modal when needed
 */
const GlobalMetaMaskHandler: React.FC = () => {
  const [showErrorRecovery, setShowErrorRecovery] = useState(false);
  const [lastError, setLastError] = useState<any>(null);

  useEffect(() => {
    const handleMetaMaskError = (event: CustomEvent) => {
      const { error, method } = event.detail;

      console.log("GlobalMetaMaskHandler: Caught MetaMask error:", {
        error,
        method,
      });

      // Show error recovery for specific error types
      if (
        error.code === -32002 ||
        error.code === 4001 ||
        error.message?.includes("Connection declined") ||
        error.message?.includes("already pending") ||
        error.message?.includes("Request already pending")
      ) {
        setLastError(error);
        setShowErrorRecovery(true);
      }
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason;

      // Check if this is a MetaMask-related error
      if (error && typeof error === "object") {
        if (
          error.code === -32002 ||
          error.code === 4001 ||
          (error.message &&
            (error.message.includes("Connection declined") ||
              error.message.includes("already pending") ||
              error.message.includes("Request already pending") ||
              error.message.includes("MetaMask")))
        ) {
          console.log(
            "GlobalMetaMaskHandler: Caught unhandled MetaMask rejection:",
            error
          );
          setLastError(error);
          setShowErrorRecovery(true);
          event.preventDefault(); // Prevent the error from being logged to console
        }
      }
    };

    const handleError = (event: ErrorEvent) => {
      const error = event.error;

      // Check if this is a MetaMask-related error
      if (error && typeof error === "object") {
        if (
          error.code === -32002 ||
          error.code === 4001 ||
          (error.message &&
            (error.message.includes("Connection declined") ||
              error.message.includes("already pending") ||
              error.message.includes("Request already pending") ||
              error.message.includes("MetaMask")))
        ) {
          console.log(
            "GlobalMetaMaskHandler: Caught global MetaMask error:",
            error
          );
          setLastError(error);
          setShowErrorRecovery(true);
          event.preventDefault(); // Prevent the error from being logged to console
        }
      }
    };

    // Listen for custom MetaMask error events
    window.addEventListener(
      "metamask-error",
      handleMetaMaskError as EventListener
    );

    // Listen for unhandled promise rejections
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    // Listen for global errors
    window.addEventListener("error", handleError);

    return () => {
      window.removeEventListener(
        "metamask-error",
        handleMetaMaskError as EventListener
      );
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection
      );
      window.removeEventListener("error", handleError);
    };
  }, []);

  const handleCloseErrorRecovery = () => {
    setShowErrorRecovery(false);
    setLastError(null);
  };

  if (!showErrorRecovery) {
    return null;
  }

  return (
    <MetaMaskErrorRecovery onClose={handleCloseErrorRecovery} autoShow={true} />
  );
};

export default GlobalMetaMaskHandler;
